import {
  Alert,
  Box,
  Button,
  Card,
  Combobox,
  Grid,
  Group,
  ScrollArea,
  Select,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core';
import { IconAlertCircle, IconInfoCircle, IconMail, IconPointer, IconSend, IconUser } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import EventSelector from '../EventSelector';
import Spinner from '../Spinner';
import TriggerEventMessage from '../TriggerEventMessage';

const SuperAdminEmailTestSend = () => {
  const fetch = useFetch();
  const { user } = useSelector((state) => state.auth);

  // Form state
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [impersonateProfileQuery, setImpersonateProfileQuery] = useState('');
  const [triggerEventAction, setTriggerEventAction] = useState();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedMailerIpId, setSelectedMailerIpId] = useState(null); // null means automatic

  // Data state
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState(null);
  const [mailerIps, setMailerIps] = useState([]);
  const [registryAvailable, setRegistryAvailable] = useState(true);
  const [mailerIpsLoading, setMailerIpsLoading] = useState(false);
  const [mailerIpsError, setMailerIpsError] = useState(null);
  const [eventActions, setEventActions] = useState([]);
  const [eventActionsLoading, setEventActionsLoading] = useState(false);
  const [eventActionsError, setEventActionsError] = useState(null);

  // Send state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(null);

  // Domain search
  const [domainSearch, setDomainSearch] = useState('');
  const domainCombobox = useCombobox({
    onDropdownClose: () => {
      domainCombobox.resetSelectedOption();
      setDomainSearch('');
    },
    onDropdownOpen: () => {
      domainCombobox.focusSearchInput();
    },
  });

  // Email search
  const [emailSearch, setEmailSearch] = useState('');

  // Build domains list from user roles
  const allDomains = useMemo(
    () =>
      user.userRoles
        .filter((role) => role.account)
        .flatMap((role) =>
          role.account.domains.map((d) => ({
            id: d.id,
            name: d.name,
            accountName: role.account.name,
            accountId: role.account.id,
          }))
        ),
    [user.userRoles]
  );

  // Filter domains based on search
  const searchTerm = domainSearch.toLowerCase().trim();
  const filteredDomains = allDomains.filter(
    (d) =>
      d.name.toLowerCase().includes(searchTerm) ||
      d.id.toString().includes(searchTerm) ||
      d.accountName.toLowerCase().includes(searchTerm) ||
      d.accountId.toString().includes(searchTerm)
  );

  // Group filtered domains by account
  const groupedDomains = filteredDomains.reduce((acc, d) => {
    if (!acc[d.accountName]) {
      acc[d.accountName] = [];
    }
    acc[d.accountName].push(d);
    return acc;
  }, {});

  // Get selected domain details
  const selectedDomain = allDomains.find((d) => d.id === selectedDomainId);

  // Load mailer IPs on mount
  useEffect(() => {
    setMailerIpsLoading(true);
    setMailerIpsError(null);
    fetch('/api/admin/mailerIps')
      .then(({ ips, registryAvailable }) => {
        setMailerIps(ips || []);
        setRegistryAvailable(registryAvailable);
        setMailerIpsLoading(false);
      })
      .catch((error) => {
        setMailerIpsError(error);
        setMailerIpsLoading(false);
      });
  }, [fetch]);

  // Load emails when domain changes
  useEffect(() => {
    if (!selectedDomainId) {
      setEmails([]);
      setSelectedEmailId(null);
      return;
    }

    const domain = allDomains.find((d) => d.id === selectedDomainId);
    if (!domain) return;

    setEmailsLoading(true);
    setEmailsError(null);
    setSelectedEmailId(null); // Reset email selection

    const params = new URLSearchParams({
      resultsPerPage: '100',
      page: '1',
    });

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/emails?${params.toString()}`)
      .then(({ emails }) => {
        setEmails(emails || []);
        setEmailsLoading(false);
      })
      .catch((error) => {
        setEmailsError(error);
        setEmailsLoading(false);
      });
  }, [selectedDomainId, fetch, allDomains]);

  // Load event actions when domain changes
  useEffect(() => {
    if (!selectedDomainId) {
      setEventActions([]);
      setTriggerEventAction(undefined);
      return;
    }

    const domain = allDomains.find((d) => d.id === selectedDomainId);
    if (!domain) return;

    setEventActionsLoading(true);
    setEventActionsError(null);
    setTriggerEventAction(undefined); // Reset event action selection

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/eventActions`)
      .then(({ eventActions }) => {
        setEventActions(eventActions || []);
        setEventActionsLoading(false);
      })
      .catch((error) => {
        setEventActionsError(error);
        setEventActionsLoading(false);
      });
  }, [selectedDomainId, fetch, allDomains]);

  // Handle send
  const handleSend = useCallback(() => {
    if (!selectedDomainId || !selectedEmailId || !recipientEmail) return;

    const domain = allDomains.find((d) => d.id === selectedDomainId);
    if (!domain) return;

    setSending(true);
    setSendError(null);
    setSendSuccess(null);

    const body = {
      sendTo: recipientEmail,
    };

    if (impersonateProfileQuery) {
      body.impersonateProfileQuery = impersonateProfileQuery;
    }

    if (triggerEventAction) {
      body.triggerEventAction = triggerEventAction;
    }

    if (selectedMailerIpId !== null) {
      body.mailerIpId = selectedMailerIpId;
    }

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/emails/${selectedEmailId}/superAdminTestSend`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then(() => {
        setSending(false);
        // Find the IP that was used
        let usedIpInfo = "Automatic (domain's pool)";
        if (selectedMailerIpId) {
          const ip = mailerIps.find((ip) => ip.id === selectedMailerIpId);
          if (ip) {
            usedIpInfo = `${ip.publicIp}`;
          }
        }
        setSendSuccess(`Test email sent successfully! Used mailer IP: ${usedIpInfo}`);
      })
      .catch((error) => {
        setSending(false);
        setSendError(error);
      });
  }, [
    selectedDomainId,
    selectedEmailId,
    recipientEmail,
    impersonateProfileQuery,
    triggerEventAction,
    selectedMailerIpId,
    fetch,
    allDomains,
    mailerIps,
  ]);

  // Filter emails based on search
  const filteredEmails = emails.filter((email) => {
    const searchLower = emailSearch.toLowerCase();
    return (
      email.name.toLowerCase().includes(searchLower) ||
      email.id.toString().includes(searchLower) ||
      (email.subject && email.subject.toLowerCase().includes(searchLower))
    );
  });

  // Group mailer IPs by pool
  const groupedIps = mailerIps.reduce((acc, ip) => {
    const pool = ip.pool || 'unknown';
    if (!acc[pool]) {
      acc[pool] = [];
    }
    acc[pool].push(ip);
    return acc;
  }, {});

  // Create mailer IP select data
  const mailerIpSelectData = [
    {
      value: 'automatic',
      label: "Automatic (domain's pool)",
    },
    ...Object.entries(groupedIps).map(([pool, ips]) => ({
      group: pool.charAt(0).toUpperCase() + pool.slice(1),
      items: ips.map((ip) => ({
        value: ip.id.toString(),
        label: `${ip.publicIp} - ${ip.status}${ip.status === 'warming' ? ` (ramp ${ip.rampStage})` : ''}`,
        disabled: !ip.live && registryAvailable,
      })),
    })),
  ];

  const canSend = selectedDomainId && selectedEmailId && recipientEmail && !sending;

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="md">
        <Alert icon={<IconInfoCircle />} mb="md">
          This send always goes through the Releva postfix path, overriding the domain's normal routing. This is
          diagnostic: no emailSend event, no tracking, no warm-up accounting.
        </Alert>

        <Grid gutter="md">
          {/* Domain selector */}
          <Grid.Col span={12}>
            <Text size="sm" fw={500} mb={4}>
              Domain
            </Text>
            <Combobox
              store={domainCombobox}
              onOptionSubmit={(value) => {
                setSelectedDomainId(parseInt(value, 10));
                domainCombobox.closeDropdown();
              }}
            >
              <Combobox.Target>
                <TextInput
                  placeholder="Select a domain..."
                  value={selectedDomain ? `${selectedDomain.name} (#${selectedDomain.id})` : ''}
                  onClick={() => domainCombobox.openDropdown()}
                  readOnly
                  rightSection={<Combobox.Chevron />}
                  style={{ cursor: 'pointer' }}
                />
              </Combobox.Target>

              <Combobox.Dropdown>
                <Combobox.Search
                  value={domainSearch}
                  onChange={(event) => setDomainSearch(event.currentTarget.value)}
                  placeholder="Search domains..."
                />
                <Combobox.Options>
                  <ScrollArea.Autosize type="auto" mah={300}>
                    {Object.entries(groupedDomains).length > 0 ? (
                      Object.entries(groupedDomains).map(([accountName, domains]) => (
                        <Combobox.Group label={accountName} key={accountName}>
                          {domains.map((d) => (
                            <Combobox.Option value={d.id.toString()} key={d.id}>
                              <Group gap="xs">
                                <Text c="dimmed" size="sm">
                                  #{d.id}
                                </Text>
                                {d.name}
                              </Group>
                            </Combobox.Option>
                          ))}
                        </Combobox.Group>
                      ))
                    ) : (
                      <Combobox.Empty>No domains found</Combobox.Empty>
                    )}
                  </ScrollArea.Autosize>
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          </Grid.Col>

          {/* Email selector */}
          <Grid.Col span={12}>
            <Text size="sm" fw={500} mb={4}>
              Email
            </Text>
            {emailsLoading ? (
              <Spinner />
            ) : emailsError ? (
              <ErrorMessage message={emailsError.message || 'Failed to load emails'} />
            ) : selectedDomainId ? (
              <>
                <TextInput
                  placeholder="Search emails..."
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  mb="xs"
                />
                <Select
                  placeholder="Select an email..."
                  value={selectedEmailId ? selectedEmailId.toString() : null}
                  onChange={(value) => setSelectedEmailId(value ? parseInt(value, 10) : null)}
                  data={filteredEmails.map((email) => ({
                    value: email.id.toString(),
                    label: `${email.name} (#${email.id})`,
                  }))}
                  searchable={false}
                  disabled={!selectedDomainId}
                />
              </>
            ) : (
              <Text c="dimmed" size="sm">
                Select a domain first
              </Text>
            )}
          </Grid.Col>

          {/* Profile to impersonate */}
          <Grid.Col span={12}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <label>
                  <IconUser size={14} style={{ marginRight: 4 }} />
                  Profile to impersonate
                </label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <TextInput
                  placeholder="Type email, phone number (with +countryCode), or profile id"
                  value={impersonateProfileQuery}
                  onChange={(e) => setImpersonateProfileQuery(e.target.value)}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  The profile you want to get the data from when sending the message. <br />
                  <br /> For example if you have
                  <br /> - "Recipient" - john.doe@example.com <br /> - "Profile to impersonate" - alice@example.com
                  <br />
                  <br />
                  You use {'{{firstName}}'} in the message you sent, we will send the message to john.doe@example.com
                  with message containing alice@example.com first name - "Alice".
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>

          {/* Trigger event */}
          {selectedDomainId && eventActions.length > 0 && (
            <Grid.Col span={12}>
              <Grid>
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <label>
                    <IconPointer size={14} style={{ marginRight: 4 }} />
                    Trigger event
                  </label>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  {eventActionsLoading ? (
                    <Spinner />
                  ) : eventActionsError ? (
                    <ErrorMessage message={eventActionsError.message || 'Failed to load event actions'} />
                  ) : (
                    <>
                      <EventSelector
                        actions={[triggerEventAction]}
                        setActions={([action]) => setTriggerEventAction(action)}
                        accountId={selectedDomain?.accountId}
                        domainId={selectedDomain?.id}
                      />
                      <Text size="xs" c="dimmed" mt="xs">
                        <TriggerEventMessage />
                      </Text>
                    </>
                  )}
                </Grid.Col>
              </Grid>
            </Grid.Col>
          )}

          {/* Recipient */}
          <Grid.Col span={12}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <label>
                  <IconMail size={14} style={{ marginRight: 4 }} />
                  Recipient
                </label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <TextInput
                  placeholder="recipient@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Where the test email will be sent
                </Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>

          {/* Mailer IP selector */}
          <Grid.Col span={12}>
            <Text size="sm" fw={500} mb={4}>
              Mailer IP
            </Text>
            {mailerIpsLoading ? (
              <Spinner />
            ) : mailerIpsError ? (
              <ErrorMessage message={mailerIpsError.message || 'Failed to load mailer IPs'} />
            ) : (
              <>
                {!registryAvailable && (
                  <Alert icon={<IconAlertCircle />} color="yellow" mb="xs">
                    Live registry is unavailable. All IPs are selectable but non-live ones may be rejected.
                  </Alert>
                )}
                <Select
                  placeholder="Select a mailer IP..."
                  value={selectedMailerIpId === null ? 'automatic' : selectedMailerIpId.toString()}
                  onChange={(value) =>
                    setSelectedMailerIpId(!value || value === 'automatic' ? null : parseInt(value, 10))
                  }
                  data={mailerIpSelectData}
                  renderOption={({ option }) => {
                    if (option.disabled) {
                      return (
                        <Group gap="xs" opacity={0.5}>
                          <Text>{option.label}</Text>
                          <Text size="xs" c="dimmed">
                            (not live)
                          </Text>
                        </Group>
                      );
                    }
                    return option.label;
                  }}
                />
              </>
            )}
          </Grid.Col>
        </Grid>

        {/* Send button and messages */}
        <Box mt="lg">
          {sendError && <ErrorMessage message={sendError.message || 'Failed to send test email'} mb="md" />}

          {sendSuccess && (
            <Alert icon={<IconSend />} color="green" mb="md">
              {sendSuccess}
            </Alert>
          )}

          <Button disabled={!canSend} onClick={handleSend} leftSection={<IconSend size={16} />}>
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </Box>
      </Card.Section>
    </Card>
  );
};

export default SuperAdminEmailTestSend;
